import { useEffect, useState } from 'react';
import { Button, Input, Select, Textarea, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, Card } from '../../../shared/components';
import { PlusIcon, TrashIcon, EditIcon } from '../../../shared/icons';
import { getHumanResourcesRepository } from '../../../shared/services/humanResources/repositoryFactory';
import { Employee, ShiftRecord, UpdateShiftRecordInput } from '../../../shared/services/humanResources/types';

const repo = getHumanResourcesRepository();

function todayInputValue(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const emptyForm = {
  employeeId: '',
  shiftDate: todayInputValue(),
  assignedEntryTime: '06:00',
  assignedHours: '8',
  operativeEntryTime: '06:00',
  operativeHours: '8',
  mealHours: '0',
  compensacion: '' as '' | 'si' | 'no',
  incapacity: '',
  unpaidLeave: '',
  paidLeave: '',
  noveltyReason: '',
  authorizedOrDeducted: '',
};

type FormState = typeof emptyForm;

const COMPENSACION_OPTIONS = [
  { value: '', label: 'No aplica (día ordinario)' },
  { value: 'si', label: 'Compensado (día libre a cambio)' },
  { value: 'no', label: 'No compensado (se paga)' },
];

/** Convierte un ShiftRecord ya guardado en el shape plano que usa el formulario (para editar). */
function recordToForm(r: ShiftRecord): FormState {
  return {
    employeeId: r.employeeId,
    shiftDate: r.shiftDate,
    assignedEntryTime: r.assignedEntryTime,
    assignedHours: String(r.assignedHours),
    operativeEntryTime: r.operativeEntryTime,
    operativeHours: String(r.operativeHours),
    mealHours: String(r.mealHours),
    compensacion: r.hasCompensatoryRest === null ? '' : r.hasCompensatoryRest ? 'si' : 'no',
    incapacity: r.incapacity ?? '',
    unpaidLeave: r.unpaidLeave ?? '',
    paidLeave: r.paidLeave ?? '',
    noveltyReason: r.noveltyReason ?? '',
    authorizedOrDeducted: r.authorizedOrDeducted ?? '',
  };
}

function formToPayload(form: FormState) {
  return {
    employeeId: form.employeeId,
    shiftDate: form.shiftDate,
    assignedEntryTime: form.assignedEntryTime,
    assignedHours: Number(form.assignedHours),
    operativeEntryTime: form.operativeEntryTime,
    operativeHours: Number(form.operativeHours),
    mealHours: Number(form.mealHours) || 0,
    hasCompensatoryRest: form.compensacion === '' ? null : form.compensacion === 'si',
    incapacity: form.incapacity.trim() || null,
    unpaidLeave: form.unpaidLeave.trim() || null,
    paidLeave: form.paidLeave.trim() || null,
    noveltyReason: form.noveltyReason.trim() || null,
    authorizedOrDeducted: form.authorizedOrDeducted.trim() || null,
  };
}

/** Campos de "Novedades" (Incapacidad/Licencias/Motivo/Autorizado) - equivalentes a las columnas
 *  AK-AO de la hoja EXTRA del Excel. Son texto libre, no afectan el cálculo (misma limitación
 *  documentada en el análisis original, punto #7). */
function NoveltyFields({ form, onChange }: { form: FormState; onChange: (patch: Partial<FormState>) => void }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Input label="Incapacidad" placeholder="Ej. 2 días - EPS" value={form.incapacity} onChange={(e) => onChange({ incapacity: e.target.value })} />
      <Input label="Licencia no remunerada" placeholder="Opcional" value={form.unpaidLeave} onChange={(e) => onChange({ unpaidLeave: e.target.value })} />
      <Input label="Licencia remunerada" placeholder="Opcional" value={form.paidLeave} onChange={(e) => onChange({ paidLeave: e.target.value })} />
      <Input label="Autorizado o descontado" placeholder="Opcional" value={form.authorizedOrDeducted} onChange={(e) => onChange({ authorizedOrDeducted: e.target.value })} />
      <div className="sm:col-span-2">
        <Textarea label="Motivo de la novedad" placeholder="Observaciones del líder sobre el turno" value={form.noveltyReason} onChange={(e) => onChange({ noveltyReason: e.target.value })} />
      </div>
    </div>
  );
}

/**
 * Pestaña "Registro diario": equivalente a la tabla TablaHoras de la hoja
 * EXTRA. Un turno Asignado (lo que debía cumplir) y un turno Operativo (lo
 * que realmente trabajó), con la misma logica de entrada+duracion del Excel.
 * El desglose diurno/nocturno/ordinario/festivo y el pago se calculan
 * automaticamente - no hay boton "Calcular" (ver Resumen).
 *
 * Incluye: filtros (empleado / rango de fechas - para localizar turnos de
 * una quincena puntual), edición de un registro ya guardado (el Excel
 * permitía corregir cualquier celda; antes aquí solo se podía borrar y
 * volver a crear) y los campos de novedades (Incapacidad, Licencias, Motivo,
 * Autorizado o Descontado) que ya existían en el modelo de datos pero no se
 * podían diligenciar desde la pantalla.
 */
export function ShiftRecordsTab() {
  const toast = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [records, setRecords] = useState<ShiftRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleting, setDeleting] = useState<ShiftRecord | null>(null);
  const [showNovelty, setShowNovelty] = useState(false);

  const [editing, setEditing] = useState<ShiftRecord | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [filterEmployeeId, setFilterEmployeeId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const load = () => {
    setIsLoading(true);
    repo
      .listShiftRecords({
        employeeId: filterEmployeeId || undefined,
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
        page: 1,
        pageSize: 200,
      })
      .then((r) => {
        setRecords(r.items);
        setTotal(r.total);
        setIsLoading(false);
      });
  };
  useEffect(() => {
    repo.listEmployees(false).then(setEmployees);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRecord = async () => {
    if (!form.employeeId || !form.shiftDate) {
      toast.show('Selecciona el empleado y la fecha.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      await repo.createShiftRecord(formToPayload(form));
      setForm((f) => ({ ...emptyForm, employeeId: f.employeeId }));
      toast.show('Registro guardado.');
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar el registro.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const openEdit = (r: ShiftRecord) => {
    setEditing(r);
    setEditForm(recordToForm(r));
  };

  const saveEdit = async () => {
    if (!editing) return;
    setIsSavingEdit(true);
    try {
      const payload = formToPayload(editForm);
      const patch: UpdateShiftRecordInput = {
        shiftDate: payload.shiftDate,
        assignedEntryTime: payload.assignedEntryTime,
        assignedHours: payload.assignedHours,
        operativeEntryTime: payload.operativeEntryTime,
        operativeHours: payload.operativeHours,
        mealHours: payload.mealHours,
        hasCompensatoryRest: payload.hasCompensatoryRest,
        incapacity: payload.incapacity,
        unpaidLeave: payload.unpaidLeave,
        paidLeave: payload.paidLeave,
        noveltyReason: payload.noveltyReason,
        authorizedOrDeducted: payload.authorizedOrDeducted,
      };
      await repo.updateShiftRecord(editing.id, patch);
      toast.show('Registro actualizado.');
      setEditing(null);
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo actualizar el registro.', 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const employeeOptions = employees.map((e) => ({ value: e.id, label: e.fullName }));
  const employeeFilterOptions = [{ value: '', label: 'Todos los empleados' }, ...employeeOptions];

  const hasNovelty = (r: ShiftRecord) => !!(r.incapacity || r.unpaidLeave || r.paidLeave || r.noveltyReason || r.authorizedOrDeducted);

  const columns: Column<ShiftRecord>[] = [
    { key: 'shiftDate', header: 'Fecha', render: (r) => r.shiftDate, sortable: true, sortValue: (r) => r.shiftDate },
    { key: 'employee', header: 'Empleado', render: (r) => r.employeeName },
    { key: 'asignado', header: 'Asignado', render: (r) => `${r.assignedEntryTime} · ${r.assignedHours}h` },
    { key: 'operativo', header: 'Operativo', render: (r) => `${r.operativeEntryTime} · ${r.operativeHours}h` },
    { key: 'festivo', header: 'Tipo día', render: (r) => <Badge tone={r.isFestive ? 'warning' : 'neutral'}>{r.isFestive ? 'Festivo' : 'Ordinario'}</Badge> },
    {
      key: 'comp',
      header: 'Compensatorio',
      render: (r) => (r.hasCompensatoryRest === null ? '—' : r.hasCompensatoryRest ? 'Sí' : 'No'),
    },
    {
      key: 'novedad',
      header: 'Novedad',
      render: (r) => (hasNovelty(r) ? <Badge tone="info">Sí</Badge> : '—'),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex items-center gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-50" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Filtros</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <Select options={employeeFilterOptions} value={filterEmployeeId} onChange={(e) => setFilterEmployeeId(e.target.value)} />
          <Input type="date" label="Desde" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
          <Input type="date" label="Hasta" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
          <div className="flex items-end gap-2">
            <Button size="md" variant="outline" fullWidth onClick={load}>
              Filtrar
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select placeholder="Empleado" options={employeeOptions} value={form.employeeId} onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))} />
        <Input type="date" value={form.shiftDate} onChange={(e) => setForm((f) => ({ ...f, shiftDate: e.target.value }))} />
        <Select options={COMPENSACION_OPTIONS} value={form.compensacion} onChange={(e) => setForm((f) => ({ ...f, compensacion: e.target.value as any }))} />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Input type="time" label="Entrada asignada" value={form.assignedEntryTime} onChange={(e) => setForm((f) => ({ ...f, assignedEntryTime: e.target.value }))} />
        <Input type="number" label="Horas asignadas" value={form.assignedHours} onChange={(e) => setForm((f) => ({ ...f, assignedHours: e.target.value }))} />
        <Input type="time" label="Entrada operativa" value={form.operativeEntryTime} onChange={(e) => setForm((f) => ({ ...f, operativeEntryTime: e.target.value }))} />
        <Input type="number" label="Horas operativas" value={form.operativeHours} onChange={(e) => setForm((f) => ({ ...f, operativeHours: e.target.value }))} />
        <Input type="number" label="Alimentación (h)" value={form.mealHours} onChange={(e) => setForm((f) => ({ ...f, mealHours: e.target.value }))} />
      </div>

      <div>
        <button type="button" className="text-sm font-semibold text-brand-600 hover:underline" onClick={() => setShowNovelty((v) => !v)}>
          {showNovelty ? 'Ocultar novedades' : 'Agregar novedad (incapacidad, licencia, observaciones)'}
        </button>
        {showNovelty && (
          <div className="mt-3">
            <NoveltyFields form={form} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
          </div>
        )}
      </div>

      <Button size="md" leftIcon={<PlusIcon width={18} height={18} />} onClick={addRecord} isLoading={isSaving}>
        Guardar registro
      </Button>

      <p className="text-xs text-ink-400">{total} registro(s) en el filtro actual.</p>
      <DataTable columns={columns} rows={records} rowKey={(r) => r.id} isLoading={isLoading} emptyTitle="Sin registros diarios" />

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar registro"
        description={`¿Eliminar el registro de ${deleting?.employeeName} del ${deleting?.shiftDate}?`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteShiftRecord(deleting.id);
          setDeleting(null);
          load();
        }}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing ? `Editar registro · ${editing.employeeName}` : ''}>
        {editing && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input type="date" label="Fecha" value={editForm.shiftDate} onChange={(e) => setEditForm((f) => ({ ...f, shiftDate: e.target.value }))} />
              <Select label="Compensación" options={COMPENSACION_OPTIONS} value={editForm.compensacion} onChange={(e) => setEditForm((f) => ({ ...f, compensacion: e.target.value as any }))} />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Input type="time" label="Entrada asignada" value={editForm.assignedEntryTime} onChange={(e) => setEditForm((f) => ({ ...f, assignedEntryTime: e.target.value }))} />
              <Input type="number" label="Horas asignadas" value={editForm.assignedHours} onChange={(e) => setEditForm((f) => ({ ...f, assignedHours: e.target.value }))} />
              <Input type="number" label="Alimentación (h)" value={editForm.mealHours} onChange={(e) => setEditForm((f) => ({ ...f, mealHours: e.target.value }))} />
              <Input type="time" label="Entrada operativa" value={editForm.operativeEntryTime} onChange={(e) => setEditForm((f) => ({ ...f, operativeEntryTime: e.target.value }))} />
              <Input type="number" label="Horas operativas" value={editForm.operativeHours} onChange={(e) => setEditForm((f) => ({ ...f, operativeHours: e.target.value }))} />
            </div>
            <div className="border-t border-ink-100 pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Novedades</p>
              <NoveltyFields form={editForm} onChange={(patch) => setEditForm((f) => ({ ...f, ...patch }))} />
            </div>
            <Button size="md" fullWidth onClick={saveEdit} isLoading={isSavingEdit}>
              Guardar cambios
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
